import { defaultLocale } from "@app/app/providers/i18n.tsx";
import {
	FxIdApplicationHandlersAuthAuthenticateToFxIdFxIdAuthenticationType,
	FxIdWebFeaturesAuthAuthenticateFromFirebaseRequest
} from "@app/shared/api/gen";
import { setAuthToken } from "@shared/api/Auth.ts";
import OpenApiClient from "@shared/api/OpenApiClient.ts";
import type { AuthResponse as VkAuthResponse } from "@vkid/sdk";
import {
	FacebookAuthProvider,
	GoogleAuthProvider,
	OAuthProvider,
	signInWithPopup,
	User as FirebaseUser
} from "firebase/auth";
import log from "loglevel";
import { getI18n } from "react-i18next";

import { getFirebaseAuth } from "./firebase";

// ... (loginWithFirebaseUser, loginWithGoogleSDK, loginWithFacebookSDK, loginWithAppleSDK - без изменений)

/**
 * Аутентифицирует пользователя на нашем бэкенде после успешного входа через Firebase.
 * @param {string | undefined} gameSystemName - Системное имя игры.
 * @param {FirebaseUser} user - Объект пользователя, полученный от Firebase.
 * @param {boolean | undefined} marketingEnabled - Флаг согласия на маркетинговые рассылки.
 */
const loginWithFirebaseUser = async (
	gameSystemName: string | undefined,
	user: FirebaseUser,
	marketingEnabled: boolean | undefined
) => {
	const data: FxIdWebFeaturesAuthAuthenticateFromFirebaseRequest = {
		GameSystemName: gameSystemName,
		Token: await user.getIdToken(),
		MarketingEnabled: marketingEnabled
	};

	try {
		log.debug("Authenticating Firebase user on the server", { gameSystemName });
		const response = await OpenApiClient.Auth.fxIdWebFeaturesAuthAuthenticateFromFirebaseEndpoint(data);
		setAuthToken(response.Token);
		log.info("Server authentication for Firebase user successful, token set");
		return response;
	} catch (err) {
		log.error("Server authentication for Firebase user failed", err);
		throw err;
	}
};

/**
 * Выполняет вход через Google с использованием Firebase.
 * @param {string} [gameSystemName] - Опциональное системное имя игры.
 */
const loginWithGoogleSDK = async (gameSystemName?: string) => {
	log.debug("Starting Google login via Firebase");
	const auth = getFirebaseAuth();
	const provider = new GoogleAuthProvider();
	const result = await signInWithPopup(auth, provider);
	return loginWithFirebaseUser(gameSystemName, result.user, undefined);
};

/**
 * Выполняет вход через Facebook с использованием Firebase.
 * @param {string} [gameSystemName] - Опциональное системное имя игры.
 */
const loginWithFacebookSDK = async (gameSystemName?: string) => {
	log.debug("Starting Facebook login via Firebase");
	const auth = getFirebaseAuth();
	const provider = new FacebookAuthProvider();
	const result = await signInWithPopup(auth, provider);
	return loginWithFirebaseUser(gameSystemName, result.user, undefined);
};

/**
 * Выполняет вход через Apple с использованием Firebase.
 * @param {string} [gameSystemName] - Опциональное системное имя игры.
 */
const loginWithAppleSDK = async (gameSystemName?: string) => {
	log.debug("Starting Apple login via Firebase");
	const auth = getFirebaseAuth();
	const provider = new OAuthProvider("apple.com");
	provider.addScope("email");
	provider.setCustomParameters({
		locale: getI18n().resolvedLanguage ?? defaultLocale
	});

	const result = await signInWithPopup(auth, provider);
	return loginWithFirebaseUser(gameSystemName, result.user, undefined);
};

/**
 * @interface VkLoginPayload
 * @description Данные, необходимые для завершения входа через VK на бэкенде.
 */
export interface VkLoginPayload {
	payload: VkAuthResponse;
	codeVerifier: string;
}

/**
 * Выполняет вход через Vkontakte, обменивая код на токен на нашем бэкенде.
 * @param {VkLoginPayload} vkPayload - Пейлоад с данными от VK ID SDK.
 * @param {string} [gameSystemName] - Опциональное системное имя игры.
 */
const loginWithVkSDK = async ({ payload, codeVerifier }: VkLoginPayload, gameSystemName?: string) => {
	log.debug("Starting Vkontakte login by exchanging code on the server", { gameSystemName });
	try {
		const response = await OpenApiClient.Auth.fxIdWebFeaturesAuthAuthenticateToFxIdEndpoint({
			AuthenticationType: FxIdApplicationHandlersAuthAuthenticateToFxIdFxIdAuthenticationType.VkId,
			// TODO: Тут не будет работать на портале по сути
			Game: gameSystemName ?? "",
			VkIdLogin: {
				Code: payload.code,
				CodeVerifier: codeVerifier,
				DeviceId: payload.device_id
			}
		});

		if (response.Error != null) {
			log.error(response.Error);
			throw new Error(response.Error.Message);
		}

		if (response.Data.Token) {
			setAuthToken(response.Data.Token);
			log.info("Server authentication for Vkontakte user successful, token set");
			log.debug("Token: %s", response.Data.Token);
		}
		return response;
	} catch (err) {
		log.error("Server authentication for Vkontakte user failed", err);
		throw err;
	}
};

/**
 * Выполняет вход через email, вызывая API эндпоинт для отправки "магической ссылки".
 * @param {string} email - Email пользователя.
 * @param {string} captchaToken - Токен Cloudflare Turnstile.
 * @param {string} [gameSystemName] - Опциональное системное имя игры.
 * @returns {Promise<any>} Промис, который разрешается с ответом от API.
 * @throws {Error} Выбрасывает ошибку в случае неудачного вызова API.
 */
const loginWithEmail = async (email: string, captchaToken: string, gameSystemName?: string) => {
	log.debug(`Sending magic link to ${email} with captcha token for game: ${gameSystemName || "N/A"}`);

	try {
		const response = await OpenApiClient.Invite.fxIdWebFeaturesInviteEmailInviteEndpoint({
			CaptchaResult: captchaToken,
			Email: email,
			GameSystemName: gameSystemName
		});

		if (response.Token) {
			setAuthToken(response.Token);
			log.info("Auth token has been set successfully after email invite");
		}

		log.debug(`Magic link for ${email} sent successfully`);
		return response;
	} catch (error) {
		log.error("Failed to send magic link via API", { email, error });
		throw error;
	}
};

/**
 * Словарь, который сопоставляет провайдера с соответствующей функцией.
 * Добавлен `vk` с реальной реализацией.
 */
export const sdkLogins = {
	google: loginWithGoogleSDK,
	apple: loginWithAppleSDK,
	email: loginWithEmail,
	facebook: loginWithFacebookSDK,
	vk: loginWithVkSDK
};
